import { createFileRoute } from "@tanstack/react-router";
import { useNavigate } from "@tanstack/react-router";
import { useEffect } from "react";
import { bookingStore } from "@/domain/booking/booking.store";
import { useBookingForm } from "@/hooks/useBookingForm";
import { useSession } from "@/hooks/useSession";
import { toast } from "sonner";

export const Route = createFileRoute("/(public)/payment/checkout")({
  component: RouteComponent,
});

function RouteComponent() {
  const navigate = useNavigate();
  const { isAuthenticated, isLoading } = useSession();
  const { bookingData, paymentStatus, hasValidBookingData, clearBooking } =
    bookingStore();

  // Récupérer chefId et menuId depuis les données persistées
  const chefId = bookingData.booking?.chefId || 0;
  const menuId = bookingData.menu?.menuId || 0;
  const minGuests = bookingData.booking?.guests || 1;

  const { processPayment } = useBookingForm(chefId, menuId, minGuests);

  useEffect(() => {
    if (isLoading) return;

    if (!isAuthenticated) {
      navigate({ to: "/connexion" });
      return;
    }

    if (paymentStatus === "pending") {
      handleAutomaticPayment();
    } else {
      navigate({ to: "/" });
    }
  }, [isAuthenticated, isLoading, paymentStatus, navigate]);

  const handleAutomaticPayment = async () => {
    if (!hasValidBookingData()) {
      toast.error("Données de réservation invalides");
      navigate({ to: "/" });
      return;
    }

    const formData = {
      booking: bookingData.booking!,
      menu: bookingData.menu!,
      customer: bookingData.customer!,
      kitchen: bookingData.kitchen!,
      terms: true,
      newsletter: true,
    };

    await processPayment(formData);
  };

  const handleError = () => {
    clearBooking();
    navigate({ to: "/" });
  };

  // États d'affichage simples
  if (isLoading || !isAuthenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mx-auto mb-4"></div>
          <p>
            {isLoading
              ? "Vérification de la connexion..."
              : "Redirection en cours..."}
          </p>
        </div>
      </div>
    );
  }

  if (paymentStatus === "processing") {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p>Traitement du paiement en cours...</p>
          <p className="text-sm text-gray-600 mt-2">Veuillez patienter</p>
        </div>
      </div>
    );
  }

  if (paymentStatus === "failed") {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-600 mb-4">
            <svg
              className="w-16 h-16 mx-auto"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                clipRule="evenodd"
              />
            </svg>
          </div>
          <p className="text-lg font-medium mb-2">Erreur de paiement</p>
          <p className="text-gray-600 mb-4">
            Une erreur est survenue lors du traitement
          </p>
          <button
            onClick={handleError}
            className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700"
          >
            Retour à l'accueil
          </button>
        </div>
      </div>
    );
  }

  if (paymentStatus === "completed") {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600 mx-auto mb-4"></div>
          <p>Redirection vers Stripe...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="text-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
        <p>Préparation du paiement...</p>
        <p className="text-sm text-gray-600 mt-2">
          Finalisation de votre réservation
        </p>
      </div>
    </div>
  );
}
